// MobileCategorySection — eine Kategorie der mobilen Deck-Ansicht: dünner
// ui/CategoryHeader (sticky unter der Editor-Kopfzeile) plus Kartenraster mit der
// vom Nutzer gewählten Spaltenzahl. Trägt bei editierbaren, echten Kategorien des
// eigenen Decks zusätzlich das ⋮-Kopf-Menü (Umbenennen/Verschieben/Löschen) —
// CategoryHeader kennt dessen Inhalt nicht, nur den `menu`-Slot.
import React, { useCallback, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react';
import CategoryHeader from '../../../../ui/CategoryHeader';
import ContextMenu from '../../../../ui/ContextMenu';
import { formatPrice, computeDeckTotals } from '../../utils/formatPrice';
import MobileDeckCard from './MobileDeckCard';
import MobileMenuTrigger from './MobileMenuTrigger';

export default function MobileCategorySection({
  group,
  columnCount,
  stickyTop,
  highlightedCardId = null,
  getCardLabels,
  selectionMode = false,
  selectedCardIds = null,
  onCardTap,
  onCardLongPress,
  canManageCategory = false,
  canMoveUp = false,
  canMoveDown = false,
  onRenameCategory,
  onMoveCategory,
  onRequestDeleteCategory,
}) {
  const cards = group?.cards || [];
  const totals = computeDeckTotals(cards);
  // Zählt hoch, wenn „Umbenennen" im ⋮-Menü gewählt wird — CategoryHeader hat
  // keinen eigenen Pencil-Button hier und braucht den programmatischen Einstieg
  // (renameRequestId-Prop, siehe ui/CategoryHeader).
  const [renameRequestId, setRenameRequestId] = useState(0);

  const handleRenameSubmit = useCallback((nextName) => {
    onRenameCategory?.(group, nextName);
  }, [onRenameCategory, group]);

  // Die Gruppe geht mit nach außen: der Viewer öffnet an dieser Stelle in der deckweiten
  // Karten-Liste und blättert von dort durch alle sichtbaren Gruppen — die Kachel selbst
  // kennt ihre Gruppe nicht.
  const handleCardTap = useCallback((card) => {
    onCardTap?.(card, group);
  }, [onCardTap, group]);

  const handleCardLongPress = useCallback((card) => {
    onCardLongPress?.(card, group);
  }, [onCardLongPress, group]);

  const menu = canManageCategory ? (
    <ContextMenu trigger={<MobileMenuTrigger label={`Kategorie „${group.name}" — Aktionen`} />}>
      <ContextMenu.Item icon={Pencil} onSelect={() => setRenameRequestId((id) => id + 1)}>
        Umbenennen
      </ContextMenu.Item>
      <ContextMenu.Item icon={ArrowUp} disabled={!canMoveUp} onSelect={() => onMoveCategory?.(group, 'up')}>
        Nach oben
      </ContextMenu.Item>
      <ContextMenu.Item icon={ArrowDown} disabled={!canMoveDown} onSelect={() => onMoveCategory?.(group, 'down')}>
        Nach unten
      </ContextMenu.Item>
      <ContextMenu.Separator />
      <ContextMenu.Item icon={Trash2} danger onSelect={() => onRequestDeleteCategory?.(group)}>
        Löschen
      </ContextMenu.Item>
    </ContextMenu>
  ) : null;

  return (
    <section className="mobile-category">
      <CategoryHeader
        name={group.name}
        count={totals.count}
        price={formatPrice(totals.value)}
        // In nicht-persistenten Gruppier-Modi tragen „Nicht im Deck"-Gruppen kein
        // `excluded`-Feld, nur `type` — beide Formen kennzeichnen dieselbe Gruppe.
        excluded={Boolean(group.excluded || group.type === 'excluded')}
        stickyTop={stickyTop}
        menu={menu}
        renameRequestId={renameRequestId}
        onRename={handleRenameSubmit}
      />
      <div
        className="mobile-category__grid"
        data-testid="mobile-category-grid"
        style={{ '--mobile-columns': columnCount }}
      >
        {cards.map((card) => {
          const cardId = card.scryfallId || card.id;
          return (
            <MobileDeckCard
              key={card.deckCardEntryId ?? cardId}
              card={card}
              labels={getCardLabels?.(card.deckCardEntryId) || []}
              // Die hervorzuhebende Kennung — frisch hinzugefügt oder Rücksprung beim
              // Schließen des Viewers — kommt je nach Aufrufer als Scryfall-ID oder als
              // Zeilen-ID zurück; beide Formen zählen als Treffer.
              highlighted={
                highlightedCardId != null &&
                (card.scryfallId === highlightedCardId || card.id === highlightedCardId)
              }
              selectionMode={selectionMode}
              selected={Boolean(selectedCardIds?.has(cardId))}
              onTap={onCardTap ? handleCardTap : undefined}
              onLongPress={onCardLongPress ? handleCardLongPress : undefined}
            />
          );
        })}
      </div>
    </section>
  );
}
